/**
 * Multi-Device Provider Library
 * Central export point for multi-device WebRTC streaming functionality
 */

// === IMPORTS FOR LOCAL USE ===
import { MultiDeviceProvider } from './MultiDeviceProvider';
import { DeviceStreamManager } from './DeviceStreamManager';
import { StreamConnectionPool } from './StreamConnectionPool';
import { MultiDeviceWebRTCConnection } from './WebRTCConnection';
import { QualityManager, createQualityManager } from './QualityManager';

// === MAIN CLASSES ===
export { MultiDeviceProvider };
export { DeviceStreamManager };
export { StreamConnectionPool };
export { MultiDeviceWebRTCConnection };
export { QualityManager, createQualityManager };

// === TYPE EXPORTS ===
export type {
  // Configuration types
  MultiDeviceProviderConfig,
  DeviceManagerConfig,
  MultiDeviceConnectionConfig,
  
  // State types
  ConnectionState,
  StreamQuality,
  StreamType,
  CameraChannel,
  
  // Event types
  StreamUpdateEvent,
  StreamCallback,
  DeviceManagerEvents,
  MultiDeviceProviderEvents,
  VoiceCommState,
  TwoWayAudioStatus,
  
  // Statistics types
  DeviceStats,
  MultiDeviceStats,
  PooledConnection,
  PoolStats
} from './types';

// === CONSTANTS ===
export { 
  DEFAULT_MULTI_DEVICE_CONFIG
} from './constants';

// === FACTORY FUNCTIONS ===

/**
 * Create a Multi-Device Provider with default configuration
 * @param maxDevices - Maximum number of devices (default: 16)
 * @returns Configured Multi-Device Provider
 */
export async function createMultiDeviceProvider(
  maxDevices: number = 16
): Promise<MultiDeviceProvider> {
  const provider = new MultiDeviceProvider({
    maxDevices,
    maxConnectionsPerDevice: 4,
    defaultQuality: 'medium',
    enableConnectionPooling: true,
    connectionTimeout: 30000,
    poolCleanupInterval: 60000,
    enableStatistics: true,
    statisticsInterval: 5000
  });
  
  await provider.initialize();
  return provider;
}

/**
 * Create a Multi-Device Provider optimized for 2x2 grid
 * @returns 2x2 optimized Multi-Device Provider
 */
export async function createGrid2x2Provider(): Promise<MultiDeviceProvider> {
  return await createMultiDeviceProvider(4);
}

/**
 * Create a Multi-Device Provider optimized for 3x3 grid
 * @returns 3x3 optimized Multi-Device Provider
 */
export async function createGrid3x3Provider(): Promise<MultiDeviceProvider> {
  return await createMultiDeviceProvider(9);
}

/**
 * Create a Multi-Device Provider optimized for 4x4 grid
 * @returns 4x4 optimized Multi-Device Provider
 */
export async function createGrid4x4Provider(): Promise<MultiDeviceProvider> {
  return await createMultiDeviceProvider(16);
}

// === VERSION INFO ===
export const MULTI_DEVICE_PROVIDER_VERSION = '1.0.0';
export const MULTI_DEVICE_PROVIDER_NAME = 'FleetA Multi-Device Provider';